import * as Dialog from '@radix-ui/react-dialog';
import { CircleAlert, X } from 'lucide-react';
import './add-profile-dialog-stitch.css';
import './workspace-auto-disabled-dialog.css';

interface WorkspaceAutoDisabledDialogProps {
  open: boolean;
  title: string;
  description: string;
  workspaceName?: string | null;
  workspaceStateHint?: string;
  checkedAt?: string | null;
  probeMessage?: string | null;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

export function WorkspaceAutoDisabledDialog({
  open,
  title,
  description,
  workspaceName,
  workspaceStateHint = '',
  checkedAt,
  probeMessage,
  confirmLabel,
  cancelLabel = '稍后处理',
  onConfirm,
  onOpenChange,
}: WorkspaceAutoDisabledDialogProps) {
  const resolvedWorkspaceName = workspaceName?.trim() || '未命名空间';
  const resolvedCheckedAt = checkedAt?.trim() || '未记录';
  const resolvedProbeMessage = probeMessage?.trim() || '认证失效或已过期';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="workspace-auto-disabled-dialog-overlay" />
        <div className="workspace-auto-disabled-dialog-shell">
          <Dialog.Content className="glass-panel workspace-auto-disabled-dialog-panel">
            <header className="workspace-auto-disabled-dialog-panel__header">
              <div className="workspace-auto-disabled-dialog-panel__header-copy">
                <Dialog.Title className="workspace-auto-disabled-dialog-panel__title">
                  {title}
                </Dialog.Title>
                <Dialog.Description className="workspace-auto-disabled-dialog-panel__description">
                  {description}
                </Dialog.Description>
              </div>

              <Dialog.Close asChild>
                <button
                  type="button"
                  className="workspace-auto-disabled-dialog-panel__close"
                  aria-label="关闭弹窗"
                >
                  <X className="h-5 w-5" strokeWidth={2.05} />
                </button>
              </Dialog.Close>
            </header>

            <main className="workspace-auto-disabled-dialog-panel__body">
              <div className="workspace-auto-disabled-dialog-panel__grid">
                <section className="soft-card workspace-auto-disabled-dialog-panel__primary-card">
                  <div className="workspace-auto-disabled-dialog-panel__field">
                    <span className="workspace-auto-disabled-dialog-panel__field-label">
                      空间名称
                    </span>
                    <strong className="workspace-auto-disabled-dialog-panel__workspace-name">
                      {resolvedWorkspaceName}
                    </strong>
                  </div>

                  <div className="workspace-auto-disabled-dialog-panel__field">
                    <span className="workspace-auto-disabled-dialog-panel__field-label">
                      当前状态
                    </span>
                    <div className="workspace-auto-disabled-dialog-panel__status-row">
                      <span className="workspace-auto-disabled-dialog-panel__status-pill">
                        <CircleAlert className="h-4 w-4" strokeWidth={1.9} />
                        已自动停用
                      </span>
                    </div>
                    {workspaceStateHint ? (
                      <p className="workspace-auto-disabled-dialog-panel__field-note">
                        {workspaceStateHint}
                      </p>
                    ) : null}
                  </div>
                </section>

                <section className="workspace-auto-disabled-dialog-panel__side">
                  <div className="soft-card workspace-auto-disabled-dialog-panel__detail-card">
                    <span className="workspace-auto-disabled-dialog-panel__field-label">
                      最近探活
                    </span>
                    <strong className="workspace-auto-disabled-dialog-panel__detail-value">
                      {resolvedCheckedAt}
                    </strong>
                    <p className="workspace-auto-disabled-dialog-panel__detail-note">
                      {resolvedProbeMessage}
                    </p>
                  </div>

                  <div className="soft-card workspace-auto-disabled-dialog-panel__detail-card">
                    <span className="workspace-auto-disabled-dialog-panel__field-label">
                      后续处理
                    </span>
                    <p className="workspace-auto-disabled-dialog-panel__detail-note">
                      系统已经先停止继续使用这个空间。先去官网核实状态，再决定是否重新启用。
                    </p>
                  </div>
                </section>
              </div>
            </main>

            <footer className="workspace-auto-disabled-dialog-panel__footer">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="secondary-button workspace-auto-disabled-dialog-panel__button"
                >
                  {cancelLabel}
                </button>
              </Dialog.Close>
              <button
                type="button"
                className="primary-button workspace-auto-disabled-dialog-panel__button"
                onClick={onConfirm}
              >
                {confirmLabel}
              </button>
            </footer>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
